import { z } from "zod";
import { RejectedCandidateSchema, ScenePlanSchema, StoryEventSchema } from "./story-event.js";
import { ProseSchema } from "./prose.js";
import { ArcDifficultySchema, BandSchema, CustodySchema, DcSchema, DispositionSchema, DocumentKindSchema, FactIdSchema, FidelitySchema, MoveTypeSchema, PersonStateSchema, RedirectKindSchema, ResistanceFormSchema } from "./primitives.js";
import { CheckSpecSchema, ConsequenceSchema, DevilsBargainSchema, ModifierSchema } from "./check-spec.js";
import { TriageResultSchema } from "./triage-result.js";
import { ArcBargainGainSchema, NarrativeDeclarationSchema } from "./narrative-arc.js";
import { NarrationDeliverySchema } from "./narrative.js";
/**
 * The event union (docs/backend_architecture.md §4.1, extended per this
 * task). This is the spine of the system: session_events is append-only and
 * IS the truth (ADR 0002); everything else is a projection of it.
 *
 * Field typing policy, consistent with triage-result.ts: payloads quoting
 * the LEARNER's own words are plain strings, never Prose. NARRATION_EMITTED
 * is the one event whose entire reason for existing is to make this
 * asymmetric — its text is Prose specifically so unvalidated narration
 * cannot enter the log (the task's explicit requirement).
 */
export const ActorSchema = z.enum(["learner", "engine", "triage", "adjudicator", "narrator", "assessor"]);
const eventBaseShape = {
    turn: z.number().int().min(0),
    actor: ActorSchema,
};
/**
 * THE CEILING (ADR 0020, idea 3): the arc phase in force when the check was priced, the DC
 * the Adjudicator proposed, and whether the phase's hold had eased. Written with the check,
 * before any die exists; the applied DC is `check_spec.dc`. The debrief and GET /audit show
 * both numbers; nothing learner-facing does.
 */
export const CheckCeilingSchema = z.strictObject({
    phase_id: z.string().min(1).nullable(),
    difficulty: ArcDifficultySchema,
    dc_proposed: DcSchema,
    eased: z.boolean(),
    arc_revision: z.number().int().positive().nullable(),
    version: z.literal(1),
});
/** The stuck counters the redirect chooser read (ADR 0020, idea 2), recorded so the debrief can say why. */
export const StuckTriggerSchema = z.strictObject({
    repeat_target: z.number().int().min(0),
    no_new_info: z.number().int().min(0),
    same_move: z.number().int().min(0),
    mentor_pings: z.number().int().min(0),
    barren_streak: z.number().int().min(0),
});
const redirectShape = {
    kind: RedirectKindSchema,
    trigger: StuckTriggerSchema,
    /** The kinds that were eligible and lost, in priority order, so the debrief explains the choice. */
    considered: z.array(RedirectKindSchema),
    rung: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    lead_id: z.string().min(1).optional(),
    npc: z.string().min(1).optional(),
    document_id: z.string().min(1).optional(),
    fact_id: FactIdSchema.optional(),
};
export const SessionEventSchema = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("NARRATIVE_DECLARATION_RECORDED"), ...eventBaseShape,
        arc_id: z.string().min(1), arc_revision: z.number().int().positive(),
        declaration: NarrativeDeclarationSchema,
    }),
    z.strictObject({
        type: z.literal("NARRATIVE_PHASE_ENTERED"), ...eventBaseShape,
        arc_id: z.string().min(1), arc_revision: z.number().int().positive(),
        phase_id: z.string().min(1), purpose: z.string().min(1),
        intensity: z.enum(["quiet", "routine", "substantive", "major"]),
        entry: ProseSchema, guidance: z.string().min(1),
        document_ids: z.array(z.string().min(1)),
    }),
    z.strictObject({
        type: z.literal("NARRATIVE_TEST_RESOLVED"), ...eventBaseShape,
        arc_id: z.string().min(1), arc_revision: z.number().int().positive(),
        test_id: z.string().min(1).nullable(),
        document_id: z.string().min(1).nullable(), receipt: ProseSchema,
        source_turn: z.number().int().nonnegative(),
    }),
    z.strictObject({
        type: z.literal("SESSION_STARTED"),
        ...eventBaseShape,
        case_id: z.string().min(1),
        case_version: z.string().min(1),
        seed: z.string().min(1),
        learner_id: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("BEAT_ADVANCED"),
        ...eventBaseShape,
        beat_id: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("INTENT_DECLARED"),
        ...eventBaseShape,
        /** The learner's own words. */
        text: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("INTENT_TRIAGED"),
        ...eventBaseShape,
        result: TriageResultSchema,
    }),
    z.strictObject({
        type: z.literal("CHECK_SPECIFIED"),
        ...eventBaseShape,
        check_spec: CheckSpecSchema,
        /** Optional on historical events; records local repairs without losing the applied spec. */
        adjudication: z.strictObject({
            origin: z.enum(["model", "sanitized", "fallback"]),
            attempts: z.number().int().min(1).max(2),
            reasons: z.array(z.string()),
        }).optional(),
        /** ADR 0020: present only when an arc ceiling was in force; absent on every earlier check. */
        ceiling: CheckCeilingSchema.optional(),
        /**
         * 19D (several priced files in one turn): the further file this check prices, derived by the
         * engine from the turn's adjudicated check before any die. Absent on the turn's own check (the
         * one the Adjudicator priced) and on every earlier log, so a reader that pairs a die and a band
         * with their check by this field reads an older log as it always did.
         */
        document_id: z.string().min(1).optional(),
    }),
    z.strictObject({
        type: z.literal("BARGAIN_OFFERED"),
        ...eventBaseShape,
        devils_bargain: DevilsBargainSchema,
        /**
         * An authored bargain (2026-09-17) names its benefit and where it was placed. Absent on
         * the older adjudicator offers, whose benefit was "what came short, clean".
         */
        gains: z.array(ArcBargainGainSchema).min(1).optional(),
        source: z.strictObject({
            arc_id: z.string().min(1),
            arc_revision: z.number().int().positive(),
            phase_id: z.string().min(1),
            bargain_id: z.string().min(1),
        }).optional(),
    }),
    z.strictObject({
        type: z.literal("BARGAIN_ACCEPTED"),
        ...eventBaseShape,
    }),
    z.strictObject({
        type: z.literal("BARGAIN_DECLINED"),
        ...eventBaseShape,
    }),
    /**
     * THE OFFER HELD ONE MORE TURN (Andre's ruling of 2026-10-27; reading pass cause 7, CM-9 a and
     * AW-7 a). Engine-owned: on a turn that does not answer the deal on the table, the learner put the
     * decision off in so many words ("before I take you up on that"), or briefed only the house about
     * the offer, so the offer stands through this turn instead of lapsing. Once per offer: the next move
     * that does not answer it lets it lapse as any move does (project.ts, arc-bargain.ts). Triage only
     * classifies; code decides the hold (Law 1). Disclosed at the audit like every event.
     */
    z.strictObject({
        type: z.literal("BARGAIN_HELD"),
        ...eventBaseShape,
        /** The engine's reading: the decision put off ("deferred"), or the house briefed and the offerer not addressed. */
        reason: z.enum(["deferred", "offerer_not_addressed"]),
    }),
    z.strictObject({
        type: z.literal("DICE_ROLLED"),
        ...eventBaseShape,
        /** Never a pre-computed band — BAND_RESOLVED is separate and derived. */
        seed_counter: z.number().int().min(0),
        raw: z.number().int().min(1).max(20),
        /** 19D: the further file whose check this die prices (CHECK_SPECIFIED.document_id); absent for the turn's own check. */
        document_id: z.string().min(1).optional(),
    }),
    z.strictObject({
        type: z.literal("BAND_RESOLVED"),
        ...eventBaseShape,
        band: BandSchema,
        /** 19D: the further file whose check this band resolves (CHECK_SPECIFIED.document_id); absent for the turn's own check. */
        document_id: z.string().min(1).optional(),
    }),
    z.strictObject({
        type: z.literal("CONSEQUENCE_APPLIED"),
        ...eventBaseShape,
        consequence: ConsequenceSchema,
        narrative_source: z.strictObject({ arc_id: z.string().min(1), revision: z.number().int().positive(), phase_id: z.string().min(1) }).optional(),
    }),
    z.strictObject({
        type: z.literal("DOCUMENT_DELIVERED"),
        ...eventBaseShape,
        document_id: z.string().min(1),
        kind: DocumentKindSchema,
        fidelity: FidelitySchema,
        fact_ids_exposed: z.array(FactIdSchema),
    }),
    z.strictObject({
        /**
         * A document already delivered this session is delivered again at a
         * strictly better fidelity (packages/db's deliverDocument decides
         * "better" — CLEAN > STALE > AGGREGATED > CONTESTED > PARTIAL, the same
         * order CLAUDE.md Law 3 itself lists them in). Distinct from
         * DOCUMENT_DELIVERED so the event log records which case happened —
         * previously, a second delivery of the same document was either a
         * primary-key crash or a silently-dropped no-op with no event at all
         * (docs/DIAGNOSIS-golden-transcript.md finding c).
         */
        type: z.literal("DOCUMENT_FIDELITY_UPGRADED"),
        ...eventBaseShape,
        document_id: z.string().min(1),
        kind: DocumentKindSchema,
        previous_fidelity: FidelitySchema,
        new_fidelity: FidelitySchema,
    }),
    z.strictObject({
        type: z.literal("FACT_REVEALED"),
        ...eventBaseShape,
        fact_id: FactIdSchema,
        /** ADR 0003: FACT_REVEALED is always downstream of a DOCUMENT_DELIVERED. */
        via_document_id: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("CLOCK_TICKED"),
        ...eventBaseShape,
        clock: z.string().min(1),
        amount: z.number().int().positive(),
        new_filled: z.number().int().min(0),
    }),
    z.strictObject({
        /**
         * Fix-order step 5: days advance on time-costly actions, not turn count.
         * Record-only — the running half-day total is re-derived from the log
         * (contracts' halfDayUnitsOf, summed), rather than this event carrying a
         * cumulative total itself. One of these is recorded per qualifying ACT
         * move (INTERVIEW/GATHER/PERSUADE/MANOEUVRE/COMMIT); ANALYSE and every
         * non-ACT intent are free and never produce one. A band's time charge is
         * its own event, TIME_CHARGED, below.
         */
        type: z.literal("TIME_COST_INCURRED"),
        ...eventBaseShape,
        move_type: MoveTypeSchema,
    }),
    z.strictObject({
        /**
         * THE ADJUDICATOR'S TIME CHARGE (Andre's rulings R1, R3 and R5 of 2026-10-29, second set). A
         * band that lands with a RESOURCE_COST on the day budget spends a real half-day of the
         * engagement's clock, recorded by the engine after that band's consequences: `units` is the
         * half-days spent, always one whatever the band declared (the engine decides how much time
         * passes, Law 1), and `resource` and `amount` are what the band declared, kept for the audit.
         * The resource itself still moves by CONSEQUENCE_APPLIED and RESOURCE_SPENT; the clock reads
         * this event alone (contracts' halfDayUnitsOf). A move's own cost stays TIME_COST_INCURRED, one
         * per costed move. Written only while PNC_RECORD_TIME_CHARGES=1 (apps/api event-writers.ts).
         */
        type: z.literal("TIME_CHARGED"),
        ...eventBaseShape,
        units: z.number().int().positive(),
        resource: z.string().min(1),
        amount: z.number().int().positive(),
    }),
    z.strictObject({
        type: z.literal("RESOURCE_SPENT"),
        ...eventBaseShape,
        resource: z.string().min(1),
        amount: z.number().int().positive(),
        new_value: z.number().int().min(0),
    }),
    z.strictObject({
        type: z.literal("NPC_DISPOSITION_CHANGED"),
        ...eventBaseShape,
        npc: z.string().min(1),
        direction: z.enum(["UP", "DOWN"]),
        new_disposition: DispositionSchema,
    }),
    z.strictObject({
        type: z.literal("RULING_RECORDED"),
        ...eventBaseShape,
        ruling: ProseSchema,
    }),
    z.strictObject({
        type: z.literal("RESISTANCE_SPENT"),
        ...eventBaseShape,
        political_capital_spent: z.number().int().positive(),
        blunted_consequence: ConsequenceSchema,
    }),
    z.strictObject({
        type: z.literal("NARRATION_EMITTED"),
        ...eventBaseShape,
        /** Prose, so unvalidated narration cannot enter the log. */
        text: ProseSchema,
        /**
         * What narratorGate did to produce `text` (packages/llm's
         * NarratorGateResult.outcome, plus "call_failed" for the one path that
         * outcome type doesn't cover — the model call itself failed, so there
         * was no candidate text for narratorGate to even run its checks
         * against, and the authored ultimate-fallback line was used instead).
         * Surfaced verbatim at GET /audit via the existing raw event dump —
         * full mechanical disclosure (CLAUDE.md Law 2) extends to whether a
         * turn's narration was model-generated or a fallback, not just to
         * dice/DC/position.
         */
        gate_outcome: z.enum(["clean", "repaired", "fallback", "call_failed"]),
        delivery: NarrationDeliverySchema.optional(),
        /**
         * What the Script Supervisor did AFTER the gate. Found live 2026-09-09:
         * a third of all narrated turns shipped the composed authored fallback
         * while `gate_outcome` said "clean", because the supervisor had rejected
         * the gate's prose and recomposed the scene — and nothing on the tape
         * said so. "accepted": shipped the gate's text; "edited": the supervisor's
         * targeted substitution(s) applied and re-validated by the gate's checks;
         * "repaired": the narrator rewrote once under the supervisor's
         * instruction and the rewrite passed the checks; "recomposed": replaced
         * with the authored composition; "rejected_kept": rejected text that was
         * already the composition, so it shipped anyway; "unavailable": the
         * supervisor call failed and the gate's text shipped unchecked. Optional
         * because events written before this field existed have none.
         */
        supervisor_outcome: z.enum(["accepted", "edited", "repaired", "recomposed", "emergency", "rejected_kept", "unavailable"]).optional(),
    }),
    z.strictObject({
        type: z.literal("HYPOTHESIS_RECORDED"),
        ...eventBaseShape,
        /** The learner's own words. */
        text: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("RECOMMENDATION_SUBMITTED"),
        ...eventBaseShape,
        /** The learner's own words. */
        text: z.string().min(1),
    }),
    /**
     * The partner's question, recorded the moment it is asked — BEFORE the
     * learner answers. Without this the question shown to the learner was never
     * persisted: the answer path regenerated it and recorded the regenerated
     * text against the answer, so the tape carried a question the learner had
     * not seen, and a reload lost the pending question entirely (found live
     * 2026-09-08). The matching CROSS_EXAM_EXCHANGE repeats the question it
     * pairs with, so the tape stays readable on its own.
     */
    z.strictObject({
        type: z.literal("CROSS_EXAM_ASKED"),
        ...eventBaseShape,
        question_number: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
        question: ProseSchema,
        delivery: NarrationDeliverySchema.optional(),
    }),
    z.strictObject({
        type: z.literal("CROSS_EXAM_EXCHANGE"),
        ...eventBaseShape,
        question_number: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
        question: ProseSchema,
        /** The learner's own words. */
        answer: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("TRUTH_REVEALED"),
        ...eventBaseShape,
        mechanism: ProseSchema,
        decoy_claim: ProseSchema,
        fell_for_decoy: z.boolean(),
    }),
    z.strictObject({
        type: z.literal("ASSESSMENT_RECORDED"),
        ...eventBaseShape,
        assessment_id: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("INVARIANT_FORCED"),
        ...eventBaseShape,
        invariant: z.enum(["CORE_FACT_GUARANTEE", "CLOCK_BOUNDS", "RESOURCE_NON_NEGATIVE", "PRESENTATION_THRESHOLD"]),
        fact_id: FactIdSchema.nullable(),
        /** The NPC channel this is delivered through in-fiction — never a system message (Law 2). */
        delivered_via_npc: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("PROMISE_SURFACED"),
        ...eventBaseShape,
        lead_id: z.string().min(1),
        promise_id: z.string().min(1),
        channel: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("LEAD_PULLED"),
        ...eventBaseShape,
        lead_id: z.string().min(1),
        progress_id: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("PAYOFF_EARNED"),
        ...eventBaseShape,
        lead_id: z.string().min(1),
        payoff_id: z.string().min(1),
        via: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("PAYOFF_NEAR_MISS"),
        ...eventBaseShape,
        lead_id: z.string().min(1),
        failed_condition: z.enum(["specificity", "prerequisite", "channel_fit", "freshness", "depends_on"]),
        detail: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("LEAD_DECAYED"),
        ...eventBaseShape,
        lead_id: z.string().min(1),
        mode: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("NUDGE_ESCALATED"),
        ...eventBaseShape,
        rung: z.union([z.literal(1), z.literal(2), z.literal(3)]),
        lead_id: z.string().min(1),
        promise_id: z.string().min(1),
    }),
    z.strictObject({
        type: z.literal("DOCUMENT_OPENED"),
        ...eventBaseShape,
        document_id: z.string().min(1),
    }),
    /**
     * The one in-fiction narration nudge (onboarding pass, 2026-09-13). The
     * engine found the learner's opening turns terse and had the mentor ask,
     * once, in his own voice, to be walked through the thinking. Audit trail
     * only: what the learner saw was narration. At most once per learner ever,
     * kept on the profile row; at most once per session, kept here.
     */
    z.strictObject({
        type: z.literal("NARRATION_NUDGED"),
        ...eventBaseShape,
        reason: z.literal("TERSE_OPENING"),
    }),
    z.strictObject({
        type: z.literal("CANON_RECORDED"),
        ...eventBaseShape,
        /** Improvised concrete details captured from shipped narration so later turns cannot contradict them. */
        entries: z.array(z.string().min(1)).min(1),
    }),
    z.strictObject({
        /**
         * THE STAGE (ADR 0014): what the narrative-to-scene compiler did with
         * this turn's lines, after the narrator gate. Every admitted story
         * event (resolved ids, verbatim evidence, storylet provenance), every
         * candidate it refused and why, the transient plan the client played,
         * and which interpreter read the prose. The world state is a fold over
         * these events (packages/environment stage.ts), so `pnpm replay` and
         * GET /audit cover the drawing as they cover the dice.
         */
        type: z.literal("STAGE_COMPILED"),
        ...eventBaseShape,
        interpreter: z.enum(["model", "fallback"]),
        events: z.array(StoryEventSchema),
        rejected: z.array(RejectedCandidateSchema),
        plan: ScenePlanSchema,
    }),
    z.strictObject({
        type: z.literal("SESSION_CLAIMED"),
        ...eventBaseShape,
        guest_id: z.string().min(1),
        user_id: z.string().min(1),
    }),
    /**
     * The presentation was refused at the gate (2026-09-17). Record-only: it
     * changes no state, and exists so the next refusal can pick a different
     * thread and phrasing, and so the audit shows what the learner was told.
     */
    z.strictObject({
        type: z.literal("PRESENTATION_DECLINED"),
        ...eventBaseShape,
        line: ProseSchema.nullable(),
    }),
    /**
     * The days are spent and the occasion goes ahead (P1, 2026-09-21). Record-only
     * for the turn machine; from this event on the presentation gate stands open,
     * so a learner with a thin file is heard and examined instead of being
     * debriefed without ever presenting. `open_threads` lets the debrief say what
     * was still thin when they did.
     */
    z.strictObject({
        type: z.literal("PRESENTATION_CALLED"),
        ...eventBaseShape,
        clock: z.string().min(1).nullable(),
        days_elapsed: z.number().int().min(0),
        day_limit: z.number().int().min(1),
        /** The refusal lines the gate would have given at the call, kept for the debrief. */
        open_threads: z.array(ProseSchema),
    }),
    /**
     * The engagement's days are spent before a recommendation was presented
     * (2026-09-17). Ends the engagement: the machine moves to DEBRIEF and the
     * session completes as "time_expired". `open_threads` are the refusal lines
     * the gate would have given at that moment, kept for the debrief.
     */
    z.strictObject({
        type: z.literal("ENGAGEMENT_TIME_EXPIRED"),
        ...eventBaseShape,
        clock: z.string().min(1).nullable(),
        days_elapsed: z.number().int().min(0),
        day_limit: z.number().int().min(1),
        open_threads: z.array(ProseSchema),
    }),
    /**
     * THE DIFFICULTY RETOOL (ADR 0020). Five record-only events: none changes where a turn
     * stands, none is read into a model input, and each is disclosed in the debrief and at
     * GET /audit. Every one is derived by a pure engine function from the log, the bible and
     * the learner's words; ASK_RECORDED's `fact_ids` also read the session's blocked-attempt
     * counts (session_facts), which no event carries, so a replay re-derives them only with
     * those counts in hand.
     *
     * ASK_RECORDED — who the learner addressed and what they asked for, once per client-
     * directed turn: the same-thread primitive the code never had. `route` says what the
     * engine did with the ask. Stage 0 records it on every ACT and plain document request
     * while PNC_RECORD_ASKS=1 (off by default: see apps/api/src/turn/ask-record.ts).
     */
    z.strictObject({
        type: z.literal("ASK_RECORDED"),
        ...eventBaseShape,
        npc: z.string().min(1).nullable(),
        document_id: z.string().min(1).nullable(),
        /** CORE facts the engine found this ask demonstrably after (pursuedCoreFacts), never a model's list. */
        fact_ids: z.array(FactIdSchema),
        move_type: MoveTypeSchema.nullable(),
        route: z.enum(["rolled", "granted", "requested", "declined", "repeat"]),
    }),
    /** Idea 1: what custody decided for a document ask, and what the world will do about it. */
    z.strictObject({
        type: z.literal("REQUEST_RESOLVED"),
        ...eventBaseShape,
        document_id: z.string().min(1),
        custody: CustodySchema,
        gated: z.boolean(),
        counterpart: z.string().min(1).nullable(),
        custodian: z.string().min(1).nullable(),
        outcome: z.enum(["granted", "rolled", "deferred", "declined", "repeat", "already_held", "ask_stands", "clarifier_asked", "clarifier_answered", "clarifier_defaulted"]),
        band: BandSchema.optional(),
        /** For a deferral: the learner turn on or after which the custodian's note lands. */
        due_turn: z.number().int().min(0).optional(),
        with_document: z.boolean().optional(),
        fidelity: FidelitySchema.optional(),
        /** Index into the bible's expensive_moves when a route-around price was appended. */
        expensive_move_index: z.number().int().min(0).optional(),
        candidates: z.array(z.string().min(1)).optional(),
        /** For a repeat: the turn whose answer still stands. */
        prior_turn: z.number().int().min(0).optional(),
    }),
    /** Ideas 4 and 5, merged: the one engine-derived state of the person a check deals with, and its one term. */
    z.strictObject({
        type: z.literal("NPC_STATE_DERIVED"),
        ...eventBaseShape,
        npc: z.string().min(1),
        state: PersonStateSchema,
        form: ResistanceFormSchema.nullable(),
        /** The inputs that fired, as short stable stems ("repeat", "in_company", "first_contact_warm"). */
        terms: z.array(z.string().min(1)),
        applied: ModifierSchema.nullable(),
        /** Why a term was not applied: "cap", "model priced <npc>", "dc already hard". */
        withheld: z.string().min(1).nullable(),
        /** Model modifiers the engine term replaced (stage 2 policy), kept for disclosure. */
        replaced: z.array(ModifierSchema),
        version: z.literal(1),
    }),
    /** Idea 2: the redirect committed before the roll, conditional on a MISS (pre-declaration). */
    z.strictObject({
        type: z.literal("REDIRECT_PLANNED"),
        ...eventBaseShape,
        ...redirectShape,
        /** Blocked attempts on the pursued CORE fact when the plan was made, so FORCED_ROUTE is explainable. */
        blocked_attempts: z.number().int().min(0).optional(),
    }),
    /** Idea 2: the redirect the world actually made, after the band. */
    z.strictObject({
        type: z.literal("REDIRECT_FIRED"),
        ...eventBaseShape,
        ...redirectShape,
    }),
    z.strictObject({
        type: z.literal("SESSION_COMPLETED"),
        ...eventBaseShape,
        outcome: z.enum(["submitted", "abandoned", "time_expired"]),
    }),
]);
/**
 * Exhaustiveness helper. A switch over SessionEvent that omits a case will
 * fail to compile at the call to assertNever, because the un-narrowed
 * remainder is not assignable to `never`. See events.test-d.ts.
 */
export function assertNever(value) {
    throw new Error(`Unhandled event type: ${JSON.stringify(value)}`);
}
//# sourceMappingURL=events.js.map